import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
// import { getKybByIdAsync } from '../kyb/fetchKybSlice';
import { getUserBusinessesAsync } from './getUserBusinessesSlice';
import { saveBusinessAsync } from './saveBusinessSlice';

interface IAddNewBusinessRequest {
  name?: string;
  category: string;
  address?: string;
  city?: string;
  state?: string;
  createdByOwner?: boolean;
  type: 'soletrader' | 'business' | string;
  email: string;
  aggregatorCode?: string;
  agentCode?: string;
}

interface IAddNewBusinessResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    category?: string;
    type?: string;
    name?: string;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    aggregatorId?: string;
    aggregatorCode?: string;
    developerId?: string;
    developerStatus?: string;
    agentCode?: null;
    isAggregator?: false;
    joinedAggregatorAt?: null;
    hasAccount?: false;
    creatorId?: string;
    creatorType?: 'owner' | 'representative' | string;
    createdAt?: string;
    updatedAt?: string;
    natureOfBusiness?: string;
    annualTurnOver?: string;
    sourceOfFunds?: string;
    socialMediaHandle?: string;
    phoneNumber1?: string;
    phoneNumber2?: string;
    proofOfAddress?: string;
    aggregatorLink?: string;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IAddNewBusinessResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const addNewBusinessAsync = createAsyncThunk(
  'business/add',
  async (
    values: IAddNewBusinessRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.post<IAddNewBusinessResponse>(
        '/add-business',
        values,
        config
      );

      if (data.data?.id && data.data?.name) {
        localStorage.setItem('businessId', data.data?.id);
        localStorage.setItem('businessName', data.data?.name);
      }

      dispatch(
        saveBusinessAsync({
          businessId: data?.data?.id ?? '',
          businessType:
            values.type === 'business' ? 'REGISTERED' : 'SOLETRADER',
        })
      );

      dispatch(getUserBusinessesAsync());
      // dispatch(getKybByIdAsync());

      if (values.type === 'business') {
        dispatch(
          showAlert({
            status: 'success',
            description: 'Business Information Submitted Successfully',
            type: 'drawer',
          })
        );
      }

      return data;
    } catch (err) {
      const error = err as AxiosError<IAddNewBusinessResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
        customMessage: 'Failed to submit business details, try again',
      });

      dispatch(
        showAlert({
          status: 'error',
          description: errorMessage,
          type: 'drawer',
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const businessAdditionSlice = createSlice({
  name: 'business/add',
  initialState,
  reducers: {
    resetBusinessAddition(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addNewBusinessAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(addNewBusinessAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IAddNewBusinessResponse;
      })
      .addCase(addNewBusinessAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IAddNewBusinessResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetBusinessAddition } = businessAdditionSlice.actions;

export default businessAdditionSlice.reducer;
